import { heroRadialPattern } from "./brandAssets";

export const cmsModuleCss = `
.cms-page {
  /* === Claro Advisors brand palette === */
  --claro-cocoa: #2c2424;
  --claro-aqua: #69c8cf;
  --claro-seafoam: #d7edf0;
  --claro-linen: #f8f7f5;
  --claro-gold: #946a3e;
  --claro-beige: #e3c9ad;

  --cocoa-90: #3a3030;
  --cocoa-70: #5c5252;
  --cocoa-50: #888080;
  --cocoa-15: #dedada;
  --aqua-shade: #4ba8af;
  --linen-shade: #efece7;
  --seafoam-shade: #b8d8dc;

  --fg-1: var(--claro-cocoa);
  --fg-2: var(--cocoa-70);
  --fg-3: var(--cocoa-50);
  --fg-on-dark: var(--claro-linen);
  --fg-on-dark-2: rgba(248, 247, 245, 0.72);
  --line-1: var(--linen-shade);
  --line-2: var(--cocoa-15);
  --line-on-dark: rgba(248, 247, 245, 0.18);

  --font-serif: "ivypresto-display", "Cormorant Garamond", Georgia, "Times New Roman", serif;
  --font-sans: var(--cms-font-family, "halyard-text", "Source Sans 3", system-ui, sans-serif);

  --wrap: var(--cms-max-width, 1200px);
  --dur: 220ms;
  --dur-slow: 480ms;
  --ease-out: cubic-bezier(0.2, 0.7, 0.2, 1);
  --shadow-2: 0 4px 12px rgba(44, 36, 36, 0.06), 0 1px 2px rgba(44, 36, 36, 0.04);
  --shadow-3: 0 18px 40px -16px rgba(44, 36, 36, 0.18), 0 4px 12px rgba(44, 36, 36, 0.06);

  background: var(--claro-linen);
  color: var(--fg-1);
  font-family: var(--font-sans);
  font-weight: 400;
  font-size: 17px;
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
@supports (interpolate-size: allow-keywords) {
  .cms-page {
    interpolate-size: allow-keywords;
  }
}
.cms-page *,
.cms-page *::before,
.cms-page *::after {
  box-sizing: border-box;
}

/* ---------- Layout primitives ---------- */
.cms-section {
  padding: 128px 0;
}
.cms-wrap {
  width: 100%;
  max-width: var(--wrap);
  margin: 0 auto;
  padding: 0 40px;
}
.cms-site-header {
  background: var(--claro-cocoa);
  border-bottom: 1px solid var(--line-on-dark);
}
.cms-site-header__inner {
  align-items: center;
  display: flex;
  min-height: 92px;
}
.cms-site-header__logo-link,
.cms-site-footer__logo-link {
  display: inline-flex;
  line-height: 0;
}
.cms-site-header__logo {
  display: block;
  height: auto;
  width: 212px;
  /* render the dark Claro mark as a light logo on the dark header */
  filter: brightness(0) invert(1);
}
.cms-site-footer {
  background: var(--claro-cocoa);
  color: var(--fg-on-dark-2);
  padding: 56px 0 40px;
}
.cms-site-footer__inner {
  align-items: center;
  display: flex;
  gap: 32px;
  justify-content: space-between;
}
.cms-site-footer__logo {
  display: block;
  height: auto;
  width: 72px;
}
.cms-page h1,
.cms-page h2,
.cms-page h3 {
  margin: 0;
  text-wrap: balance;
}
.cms-page p {
  margin: 0;
}
.cms-disclosure-footnote-ref {
  color: inherit;
  font-weight: 700;
  line-height: 0;
  margin-left: 2px;
  text-decoration: none;
}
.cms-disclosure-footnote-ref:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 3px;
}

/* serif display headings, light weight — the Claro editorial voice */
.cms-page h1,
.cms-page h2 {
  font-family: var(--font-serif);
  font-weight: 300;
  letter-spacing: -0.012em;
  line-height: 1.08;
  color: var(--fg-1);
}
.cms-page h2 {
  font-size: clamp(34px, 4.4vw, 56px);
}

/* eyebrow + rule + section index, the brand's signature header pairing */
.cms-eyebrow-row {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 22px;
}
.cms-eyebrow {
  font-family: var(--font-sans);
  font-weight: 500;
  font-size: 12px;
  letter-spacing: 0.18em;
  line-height: 1;
  text-transform: uppercase;
  color: var(--fg-3);
  white-space: nowrap;
}
.cms-eyebrow-row::after {
  content: "";
  flex: 1;
  height: 1px;
  background: var(--line-1);
}
.cms-eyebrow-row__ix {
  font-family: var(--font-serif);
  font-weight: 300;
  font-size: 15px;
  letter-spacing: 0.04em;
  color: var(--claro-aqua);
}

/* tone modifiers for full-bleed section backgrounds */
.cms-tone-dark {
  background: var(--claro-cocoa);
  color: var(--fg-on-dark);
}
.cms-tone-dark h1,
.cms-tone-dark h2,
.cms-tone-dark h3 {
  color: var(--claro-linen);
}
.cms-tone-dark .cms-eyebrow {
  color: rgba(248, 247, 245, 0.6);
}
.cms-tone-dark .cms-eyebrow-row::after {
  background: var(--line-on-dark);
}
.cms-tone-tint {
  background: var(--claro-seafoam);
}

.cms-text-link {
  align-items: center;
  color: var(--claro-cocoa);
  display: inline-flex;
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 500;
  gap: 8px;
  letter-spacing: 0.04em;
  margin-top: 24px;
  text-decoration: none;
  text-transform: uppercase;
  border-bottom: 1px solid currentColor;
  padding-bottom: 4px;
  transition: color var(--dur) var(--ease-out), border-color var(--dur) var(--ease-out);
}
.cms-text-link:hover {
  color: var(--claro-aqua);
  border-bottom-color: var(--claro-aqua);
}
.cms-tone-dark .cms-text-link {
  color: var(--claro-linen);
}
.cms-tone-dark .cms-text-link:hover {
  color: var(--claro-aqua);
}

/* shared button */
.cms-btn {
  align-items: center;
  border: 1px solid var(--claro-aqua);
  background: var(--claro-aqua);
  border-radius: 4px;
  color: var(--claro-cocoa);
  display: inline-flex;
  font-family: var(--font-sans);
  font-size: 15px;
  font-weight: 500;
  gap: 10px;
  letter-spacing: 0.04em;
  padding: 18px 36px;
  text-decoration: none;
  text-transform: uppercase;
  transition: background var(--dur) var(--ease-out), color var(--dur) var(--ease-out), border-color var(--dur) var(--ease-out);
}
.cms-btn:hover {
  background: var(--aqua-shade);
  border-color: var(--aqua-shade);
  color: var(--claro-linen);
}
.cms-btn__arrow {
  display: inline-block;
  transition: transform var(--dur) var(--ease-out);
}
.cms-btn:hover .cms-btn__arrow {
  transform: translateX(4px);
}

/* ---------- Hero ---------- */
.cms-hero {
  position: relative;
  overflow: hidden;
  background: var(--claro-cocoa);
  color: var(--claro-linen);
}
.cms-hero::before {
  content: "";
  position: absolute;
  top: 50%;
  right: -14%;
  transform: translateY(-50%);
  width: 920px;
  height: 920px;
  max-width: 90vw;
  background: url("${heroRadialPattern}") center / contain no-repeat;
  filter: brightness(1.6);
  opacity: 0.5;
  pointer-events: none;
  z-index: 0;
}
.cms-hero .cms-wrap {
  position: relative;
  z-index: 1;
}
.cms-hero__layout {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(280px, 0.95fr);
  gap: 72px;
  align-items: center;
  padding: 130px 0 120px;
}
.cms-hero__content {
  max-width: 640px;
}
.cms-hero__eyebrow {
  align-items: center;
  color: var(--claro-aqua);
  display: inline-flex;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  gap: 14px;
  letter-spacing: 0.16em;
  margin: 0 0 28px;
  text-transform: uppercase;
}
.cms-hero__eyebrow-rule {
  background: currentColor;
  display: inline-block;
  height: 1px;
  width: 32px;
  opacity: 0.7;
}
.cms-hero h1 {
  font-family: var(--font-serif);
  font-size: clamp(46px, 6.4vw, 92px);
  font-weight: 300;
  letter-spacing: -0.015em;
  line-height: 1.03;
  color: var(--claro-linen);
}
.cms-hero__body {
  color: var(--fg-on-dark-2);
  font-size: clamp(18px, 2vw, 21px);
  font-weight: 300;
  line-height: 1.5;
  margin: 30px 0 0;
  max-width: 600px;
}
.cms-hero__image {
  margin: 0;
  position: relative;
}
.cms-hero__image img {
  aspect-ratio: 4 / 5;
  border-radius: 14px;
  display: block;
  object-fit: cover;
  width: 100%;
  box-shadow: var(--shadow-3);
}
.cms-hero__image figcaption {
  color: var(--fg-on-dark-2);
  font-size: 13px;
  margin-top: 16px;
}

/* ---------- CTA band ---------- */
.cms-cta {
  position: relative;
  overflow: hidden;
  background: var(--claro-cocoa);
  color: var(--claro-linen);
  text-align: center;
}
.cms-cta::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    radial-gradient(120% 120% at 50% 120%, color-mix(in srgb, var(--claro-gold) 28%, transparent) 0%, transparent 60%),
    radial-gradient(90% 90% at 50% -10%, color-mix(in srgb, var(--claro-aqua) 14%, transparent) 0%, transparent 55%);
  opacity: 0.6;
  pointer-events: none;
}
.cms-cta .cms-wrap {
  position: relative;
  z-index: 1;
  padding-top: 120px;
  padding-bottom: 120px;
}
.cms-cta__title {
  font-family: var(--font-serif);
  font-weight: 300;
  font-size: clamp(40px, 6vw, 80px);
  line-height: 1.05;
  color: var(--claro-linen);
  margin: 0 auto;
  max-width: 14ch;
}
.cms-cta .cms-btn {
  margin-top: 44px;
}

/* ---------- Advisor profile ---------- */
.cms-advisor-profile__layout {
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 80px;
  align-items: start;
}
.cms-advisor-profile__image img {
  aspect-ratio: 4 / 5;
  border-radius: 14px;
  display: block;
  object-fit: cover;
  width: 100%;
}
.cms-advisor-profile__image figcaption {
  color: var(--fg-3);
  font-size: 13px;
  margin-top: 12px;
}
.cms-advisor-profile__credentials {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 20px 0 0;
  padding: 0;
}
.cms-advisor-profile__credentials li {
  background: oklab(0.553422 -0.0799685 -0.040609 / 0.1);
  border-radius: 2px;
  padding: 4px 8px;
  color: var(--fg-2);
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.cms-advisor-profile__credentials sup {
  font-size: 0.68em;
  letter-spacing: 0;
  line-height: 0;
  margin-left: 0.02em;
}
.cms-advisor-profile__name {
  font-family: var(--font-serif);
  font-weight: 300;
  font-size: clamp(32px, 4vw, 48px);
}
.cms-advisor-profile__title {
  color: var(--fg-2);
  font-size: 18px;
  margin: 12px 0 0;
}
.cms-advisor-profile__bio {
  color: var(--fg-2);
  font-size: 18px;
  line-height: 1.6;
  margin: 24px 0 0;
  white-space: pre-line;
}
.cms-advisor-profile__links {
  display: flex;
  flex-wrap: wrap;
  gap: 14px 24px;
  margin-top: 28px;
}
.cms-advisor-profile__links a {
  color: var(--claro-cocoa);
  font-family: var(--font-sans);
  font-weight: 500;
  font-size: 15px;
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: border-color var(--dur) var(--ease-out), color var(--dur) var(--ease-out);
}
.cms-advisor-profile__links a:hover {
  color: var(--claro-aqua);
  border-bottom-color: var(--claro-aqua);
}
.cms-advisor-profile__links span {
  color: var(--fg-3);
  font-size: 15px;
}

/* ---------- Narrative / approach pillars ---------- */
.cms-narrative h2 {
  max-width: 880px;
}
.cms-narrative__body {
  color: var(--fg-2);
  font-size: 18px;
  line-height: 1.6;
  margin: 24px 0 0;
  max-width: 720px;
}
.cms-narrative__pillars {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  margin-top: 64px;
}
.cms-narrative__pillar {
  border-left: 1px solid var(--seafoam-shade);
  padding: 0 40px;
}
.cms-narrative__pillar:first-child {
  border-left: 0;
  padding-left: 0;
}
.cms-narrative__pillar-num {
  font-family: var(--font-serif);
  font-weight: 300;
  font-size: 22px;
  color: var(--claro-gold);
}
.cms-narrative__pillar p {
  color: var(--fg-2);
  font-size: 16px;
  line-height: 1.6;
  margin: 16px 0 0;
}

/* ---------- Service grid ---------- */
.cms-services__lead {
  color: var(--fg-2);
  font-size: 18px;
  line-height: 1.6;
  margin: 18px 0 0;
  max-width: 720px;
}
.cms-services__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  margin-top: 64px;
  background: var(--line-2);
  border: 1px solid var(--line-2);
  border-radius: 8px;
  overflow: hidden;
}
.cms-services__item {
  background: #ffffff;
  padding: 40px 36px 44px;
  transition: background var(--dur) var(--ease-out);
}
.cms-services__item:hover {
  background: var(--claro-linen);
}
.cms-services__item h3 {
  font-family: var(--font-serif);
  font-weight: 400;
  font-size: 23px;
  margin: 0 0 12px;
}
.cms-services__item p {
  color: var(--fg-2);
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
}
.cms-services__item a {
  color: var(--claro-gold);
  display: inline-block;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.04em;
  margin-top: 16px;
  text-decoration: none;
  text-transform: uppercase;
}

/* ---------- Audience grid (dark) ---------- */
.cms-audiences h2 {
  max-width: 760px;
}
.cms-audiences__body {
  color: var(--fg-on-dark-2);
  font-size: 18px;
  line-height: 1.6;
  margin: 18px 0 0;
  max-width: 720px;
}
.cms-audiences__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1px;
  margin-top: 64px;
  background: var(--line-on-dark);
  border: 1px solid var(--line-on-dark);
}
.cms-audiences__item {
  background: var(--claro-cocoa);
  padding: 44px 40px;
  transition: background var(--dur) var(--ease-out);
}
.cms-audiences__item:hover {
  background: var(--cocoa-90);
}
.cms-audiences__item h3 {
  font-family: var(--font-serif);
  font-weight: 400;
  font-size: 24px;
  color: var(--claro-linen);
  margin: 0 0 12px;
}
.cms-audiences__item p {
  color: var(--fg-on-dark-2);
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
}
.cms-audiences__item a {
  color: var(--claro-aqua);
  display: inline-block;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.04em;
  margin-top: 16px;
  text-decoration: none;
  text-transform: uppercase;
}

/* ---------- Design-focused modules ---------- */
.cms-info-card-grid h2 {
  max-width: 840px;
}
.cms-info-card-grid__lead,
.cms-media-text__body,
.cms-process-steps__intro p,
.cms-resource-list__intro p {
  color: var(--fg-2);
  font-size: 18px;
  line-height: 1.6;
  margin: 18px 0 0;
  max-width: 720px;
}
.cms-info-card-grid--tone-cocoa .cms-info-card-grid__lead {
  color: var(--fg-on-dark-2);
}
.cms-info-card-grid--tone-seafoam {
  background: var(--claro-seafoam);
}
.cms-info-card-grid__grid {
  margin-top: 64px;
}
.cms-info-card-grid--layout-left .cms-wrap {
  align-items: start;
  display: grid;
  gap: 64px;
  grid-template-columns: minmax(240px, 0.36fr) minmax(0, 0.64fr);
}
.cms-info-card-grid--layout-centered .cms-info-card-grid__intro {
  margin: 0 auto;
  max-width: 840px;
  text-align: center;
}
.cms-info-card-grid--layout-centered .cms-eyebrow-row {
  justify-content: center;
}
.cms-info-card-grid--layout-centered .cms-eyebrow-row::after {
  display: none;
}
.cms-info-card-grid--layout-centered h2,
.cms-info-card-grid--layout-centered .cms-info-card-grid__lead {
  margin-left: auto;
  margin-right: auto;
}
.cms-info-card-grid--layout-grid .cms-info-card-grid__grid {
  background: var(--line-2);
  border: 1px solid var(--line-2);
  border-radius: 8px;
  display: grid;
  gap: 1px;
  grid-template-columns: repeat(6, 1fr);
  overflow: hidden;
}
.cms-info-card-grid--tone-cocoa.cms-info-card-grid--layout-grid .cms-info-card-grid__grid {
  background: var(--line-on-dark);
  border-color: var(--line-on-dark);
}
.cms-info-card-grid--tone-seafoam.cms-info-card-grid--layout-grid .cms-info-card-grid__grid {
  background: var(--seafoam-shade);
  border-color: var(--seafoam-shade);
}
.cms-info-card-grid--layout-grid .cms-info-card-grid__item {
  background: #ffffff;
  grid-column: span 2;
  padding: 40px 36px 44px;
  transition: background var(--dur) var(--ease-out);
}
.cms-info-card-grid--layout-grid .cms-info-card-grid__item:nth-child(3n + 1):last-child {
  grid-column: 3 / span 2;
}
.cms-info-card-grid--layout-grid .cms-info-card-grid__item:nth-child(3n + 1):nth-last-child(2) {
  grid-column: 2 / span 2;
}
.cms-info-card-grid--layout-grid .cms-info-card-grid__grid[data-card-count="4"] {
  grid-template-columns: repeat(2, 1fr);
}
.cms-info-card-grid--layout-grid .cms-info-card-grid__grid[data-card-count="4"] .cms-info-card-grid__item {
  grid-column: auto;
}
.cms-info-card-grid--tone-cocoa.cms-info-card-grid--layout-grid .cms-info-card-grid__item {
  background: var(--claro-cocoa);
}
.cms-info-card-grid--tone-seafoam.cms-info-card-grid--layout-grid .cms-info-card-grid__item {
  background: rgba(255, 255, 255, 0.72);
}
.cms-info-card-grid--layout-grid .cms-info-card-grid__item:hover {
  background: var(--claro-linen);
}
.cms-info-card-grid--tone-cocoa.cms-info-card-grid--layout-grid .cms-info-card-grid__item:hover {
  background: var(--cocoa-90);
}
.cms-info-card-grid--tone-seafoam.cms-info-card-grid--layout-grid .cms-info-card-grid__item:hover {
  background: rgba(255, 255, 255, 0.9);
}
.cms-info-card-grid--layout-left .cms-info-card-grid__grid {
  background: var(--line-2);
  border: 1px solid var(--line-2);
  border-radius: 8px;
  display: grid;
  gap: 1px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 0;
  overflow: hidden;
}
.cms-info-card-grid--tone-cocoa.cms-info-card-grid--layout-left .cms-info-card-grid__grid {
  background: var(--line-on-dark);
  border-color: var(--line-on-dark);
}
.cms-info-card-grid--tone-seafoam.cms-info-card-grid--layout-left .cms-info-card-grid__grid {
  background: var(--seafoam-shade);
  border-color: var(--seafoam-shade);
}
.cms-info-card-grid--layout-left .cms-info-card-grid__item {
  background: #ffffff;
  padding: 34px 30px 38px;
  transition: background var(--dur) var(--ease-out);
}
.cms-info-card-grid--tone-cocoa.cms-info-card-grid--layout-left .cms-info-card-grid__item {
  background: var(--claro-cocoa);
}
.cms-info-card-grid--tone-seafoam.cms-info-card-grid--layout-left .cms-info-card-grid__item {
  background: rgba(255, 255, 255, 0.72);
}
.cms-info-card-grid--layout-left .cms-info-card-grid__item:hover {
  background: var(--claro-linen);
}
.cms-info-card-grid--tone-cocoa.cms-info-card-grid--layout-left .cms-info-card-grid__item:hover {
  background: var(--cocoa-90);
}
.cms-info-card-grid--tone-seafoam.cms-info-card-grid--layout-left .cms-info-card-grid__item:hover {
  background: rgba(255, 255, 255, 0.9);
}
.cms-info-card-grid--layout-centered .cms-info-card-grid__grid {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 56px auto 0;
  max-width: 1020px;
}
.cms-info-card-grid--layout-centered .cms-info-card-grid__item {
  background: #ffffff;
  border: 1px solid var(--line-1);
  border-radius: 8px;
  padding: 34px 28px 38px;
  text-align: center;
  transition: background var(--dur) var(--ease-out), border-color var(--dur) var(--ease-out);
}
.cms-info-card-grid--tone-cocoa.cms-info-card-grid--layout-centered .cms-info-card-grid__item {
  background: rgba(248, 247, 245, 0.06);
  border-color: var(--line-on-dark);
}
.cms-info-card-grid--tone-seafoam.cms-info-card-grid--layout-centered .cms-info-card-grid__item {
  background: rgba(255, 255, 255, 0.72);
  border-color: var(--seafoam-shade);
}
.cms-info-card-grid--layout-centered .cms-info-card-grid__item:hover {
  background: var(--claro-linen);
  border-color: var(--line-2);
}
.cms-info-card-grid--tone-cocoa.cms-info-card-grid--layout-centered .cms-info-card-grid__item:hover {
  background: rgba(248, 247, 245, 0.1);
}
.cms-info-card-grid--tone-seafoam.cms-info-card-grid--layout-centered .cms-info-card-grid__item:hover {
  background: rgba(255, 255, 255, 0.9);
}
.cms-info-card-grid--layout-columns .cms-info-card-grid__grid {
  display: flex;
  flex-wrap: wrap;
  gap: 40px;
  justify-content: center;
}
.cms-info-card-grid--layout-columns .cms-info-card-grid__item {
  border-left: 1px solid var(--line-2);
  flex: 0 1 calc((100% - 80px) / 3);
  min-width: 0;
  padding: 0 0 0 28px;
}
.cms-info-card-grid--layout-columns .cms-info-card-grid__grid[data-card-count="4"] .cms-info-card-grid__item {
  flex-basis: calc((100% - 40px) / 2);
}
.cms-info-card-grid--tone-cocoa.cms-info-card-grid--layout-columns .cms-info-card-grid__item {
  border-left-color: var(--line-on-dark);
}
.cms-info-card-grid--tone-seafoam.cms-info-card-grid--layout-columns .cms-info-card-grid__item {
  border-left-color: var(--seafoam-shade);
}
.cms-info-card-grid--layout-columns .cms-info-card-grid__grid[data-card-count="5"] .cms-info-card-grid__item:nth-child(4) {
  border-left: 0;
}
.cms-info-card-grid--layout-rule-list .cms-info-card-grid__grid {
  border-top: 1px solid var(--line-2);
  display: block;
  margin-top: 56px;
}
.cms-info-card-grid--tone-cocoa.cms-info-card-grid--layout-rule-list .cms-info-card-grid__grid {
  border-top-color: var(--line-on-dark);
}
.cms-info-card-grid--tone-seafoam.cms-info-card-grid--layout-rule-list .cms-info-card-grid__grid {
  border-top-color: var(--seafoam-shade);
}
.cms-info-card-grid--layout-rule-list .cms-info-card-grid__item {
  border-bottom: 1px solid var(--line-2);
  display: grid;
  gap: 32px;
  grid-template-columns: minmax(180px, 0.35fr) minmax(0, 0.65fr);
  padding: 30px 0;
}
.cms-info-card-grid--tone-cocoa.cms-info-card-grid--layout-rule-list .cms-info-card-grid__item {
  border-bottom-color: var(--line-on-dark);
}
.cms-info-card-grid--tone-seafoam.cms-info-card-grid--layout-rule-list .cms-info-card-grid__item {
  border-bottom-color: var(--seafoam-shade);
}
.cms-info-card-grid__item h3,
.cms-process-steps__item h3,
.cms-resource-list__item h3 {
  font-family: var(--font-serif);
  font-size: 23px;
  font-weight: 400;
  margin: 0 0 12px;
}
.cms-info-card-grid--tone-cocoa .cms-info-card-grid__item h3 {
  color: var(--claro-linen);
}
.cms-info-card-grid__item p,
.cms-process-steps__item p,
.cms-resource-list__item p {
  color: var(--fg-2);
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
}
.cms-info-card-grid--tone-cocoa .cms-info-card-grid__item p {
  color: var(--fg-on-dark-2);
}
.cms-info-card-grid__item a,
.cms-resource-list__item a {
  color: var(--claro-gold);
  display: inline-block;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.04em;
  margin-top: 16px;
  text-decoration: none;
  text-transform: uppercase;
}
.cms-info-card-grid--tone-cocoa .cms-info-card-grid__item a {
  color: var(--claro-aqua);
}

.cms-media-text__layout {
  align-items: center;
  display: grid;
  gap: 72px;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 0.85fr);
}
.cms-media-text--image-left .cms-media-text__content {
  order: 2;
}
.cms-media-text__image {
  margin: 0;
}
.cms-media-text__image img,
.cms-quote-section__image img {
  aspect-ratio: 4 / 5;
  border-radius: 14px;
  display: block;
  object-fit: cover;
  width: 100%;
}
.cms-media-text__image figcaption,
.cms-quote-section__image figcaption {
  color: var(--fg-3);
  font-size: 13px;
  margin-top: 12px;
}

.cms-process-steps__intro {
  max-width: 780px;
}
.cms-process-steps__list {
  border-top: 1px solid var(--seafoam-shade);
  margin-top: 56px;
}
.cms-process-steps__item {
  border-bottom: 1px solid var(--seafoam-shade);
  display: grid;
  gap: 32px;
  grid-template-columns: 88px minmax(0, 1fr);
  padding: 30px 0;
}
.cms-process-steps__number {
  color: var(--claro-gold);
  font-family: var(--font-serif);
  font-size: 24px;
  font-weight: 300;
}

.cms-quote-section__layout {
  align-items: center;
  display: grid;
  gap: 72px;
  grid-template-columns: minmax(280px, 0.65fr) minmax(0, 1fr);
}
.cms-quote-section--no-image .cms-quote-section__layout {
  grid-template-columns: minmax(0, 4fr) minmax(0, 1fr);
}
.cms-quote-section--layout-centered .cms-quote-section__layout {
  display: block;
}
.cms-quote-section--layout-centered .cms-quote-section__content {
  margin: 0 auto;
  max-width: 880px;
  text-align: center;
}
.cms-quote-section--layout-centered .cms-eyebrow-row {
  justify-content: center;
}
.cms-quote-section--layout-centered .cms-eyebrow-row::after {
  display: none;
}
.cms-quote-section__image {
  margin: 0;
}
.cms-quote-section blockquote {
  color: var(--claro-linen);
  font-family: var(--font-serif);
  font-size: clamp(34px, 5vw, 60px);
  font-weight: 300;
  line-height: 1.08;
  margin: 0;
}
.cms-quote-section--layout-centered blockquote {
  font-size: clamp(28px, 3.4vw, 44px);
  line-height: 1.18;
}
.cms-quote-section__attribution {
  color: var(--fg-on-dark-2);
  display: flex;
  flex-direction: column;
  font-size: 16px;
  gap: 4px;
  margin: 28px 0 0;
}
.cms-quote-section__attribution strong {
  color: var(--claro-linen);
  font-weight: 500;
}
.cms-quote-section--layout-centered .cms-quote-section__attribution {
  align-items: center;
  margin-top: 24px;
}
.cms-text-link--on-dark {
  color: var(--claro-aqua);
}

.cms-resource-list__layout {
  display: grid;
  gap: 72px;
  grid-template-columns: 0.72fr 1.28fr;
}
.cms-resource-list__items {
  border-top: 1px solid var(--line-1);
}
.cms-resource-list__item {
  align-items: start;
  border-bottom: 1px solid var(--line-1);
  display: grid;
  gap: 28px;
  grid-template-columns: minmax(0, 1fr) auto;
  padding: 28px 0;
}
.cms-resource-list__item a {
  margin-top: 3px;
  white-space: nowrap;
}

/* ---------- Important Disclosures ---------- */
.cms-important-disclosures {
  background: var(--claro-linen);
  border-top: 1px solid var(--line-1);
  border-bottom: 1px solid var(--line-1);
}
.cms-important-disclosures__layout {
  display: grid;
  grid-template-columns: 0.7fr 1.3fr;
  gap: 72px;
  align-items: start;
}
.cms-important-disclosures__rule {
  background: var(--claro-aqua);
  border-radius: 1px;
  display: block;
  height: 3px;
  margin-bottom: 28px;
  width: 56px;
}
.cms-important-disclosures__header h2 {
  font-size: clamp(34px, 3.8vw, 52px);
}
.cms-important-disclosures__content {
  color: var(--fg-2);
  font-size: 15px;
  font-weight: 300;
  line-height: 1.78;
}
.cms-important-disclosures__intro p {
  margin: 0;
}
.cms-important-disclosures__intro p + p {
  margin-top: 18px;
}
.cms-important-disclosures__footnotes {
  border-top: 1px solid var(--line-2);
  list-style: none;
  margin: 40px 0 0;
  padding: 0;
}
.cms-important-disclosures__footnote {
  border-bottom: 1px solid var(--line-1);
  column-gap: 24px;
  display: grid;
  grid-template-columns: 2.5ch minmax(0, 1fr);
  padding: 24px 0;
  scroll-margin-top: 24px;
}
.cms-important-disclosures__footnote:last-child {
  border-bottom: 0;
}
/* the reference number ties each note back to its [[key]] citation in the body */
.cms-important-disclosures__footnote-number {
  color: var(--claro-aqua);
  font-family: var(--font-serif);
  font-size: 22px;
  font-weight: 300;
  line-height: 1;
}
.cms-important-disclosures__footnote-label {
  color: var(--fg-2);
  display: block;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.08em;
  margin-bottom: 10px;
  text-transform: uppercase;
}
.cms-important-disclosures__footnote-body {
  color: var(--fg-3);
  display: block;
  font-size: 14px;
  font-weight: 300;
  line-height: 1.72;
}
.cms-important-disclosures__footnote:target {
  outline: 2px solid var(--claro-aqua);
  outline-offset: 6px;
}

/* ---------- FAQ ---------- */
.cms-faq__layout {
  display: grid;
  grid-template-columns: 0.7fr 1.3fr;
  gap: 72px;
  align-items: start;
}
.cms-faq__intro h2 {
  font-size: clamp(30px, 3.4vw, 44px);
}
.cms-faq__list {
  border-top: 1px solid var(--line-1);
}
.cms-faq__item {
  border-bottom: 1px solid var(--line-1);
}
/* Native disclosure widget styled as the Claro accordion — no client JS. */
.cms-faq__question {
  align-items: flex-start;
  color: var(--fg-1);
  cursor: pointer;
  display: flex;
  font-family: var(--font-serif);
  font-size: 21px;
  font-weight: 400;
  gap: 24px;
  justify-content: space-between;
  line-height: 1.3;
  list-style: none;
  padding: 28px 0;
}
.cms-faq__question::-webkit-details-marker {
  display: none;
}
.cms-faq__question:focus-visible {
  outline: 2px solid var(--claro-aqua);
  outline-offset: 4px;
}
.cms-faq__marker {
  flex: 0 0 22px;
  height: 22px;
  margin-top: 5px;
  position: relative;
  width: 22px;
}
.cms-faq__marker::before,
.cms-faq__marker::after {
  background: var(--claro-aqua);
  content: "";
  position: absolute;
  transition: transform var(--dur) var(--ease-out), opacity var(--dur) var(--ease-out);
}
.cms-faq__marker::before {
  left: 0;
  top: 10px;
  height: 1.5px;
  width: 22px;
}
.cms-faq__marker::after {
  left: 10px;
  top: 0;
  height: 22px;
  width: 1.5px;
}
.cms-faq__item[open] .cms-faq__marker::after {
  opacity: 0;
  transform: scaleY(0);
}
.cms-faq__answer {
  overflow: hidden;
}
.cms-faq__answer p {
  color: var(--fg-2);
  font-size: 16px;
  line-height: 1.65;
  margin: 0;
  padding: 0 46px 30px 0;
}
/* Smooth open/close where the engine supports it; snaps gracefully otherwise. */
.cms-faq__item::details-content {
  block-size: 0;
  opacity: 0;
  overflow: hidden;
  transition: block-size var(--dur-slow) var(--ease-out), opacity var(--dur-slow) var(--ease-out),
    content-visibility var(--dur-slow) allow-discrete;
}
.cms-faq__item[open]::details-content {
  block-size: auto;
  opacity: 1;
}

/* ---------- Contact ---------- */
.cms-contact__layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: start;
}
.cms-contact__body {
  color: var(--fg-2);
  font-size: 20px;
  font-weight: 300;
  line-height: 1.5;
  margin: 22px 0 0;
  max-width: 420px;
}
.cms-contact__details {
  margin-top: 40px;
}
.cms-contact__row {
  border-top: 1px solid var(--seafoam-shade);
  display: flex;
  gap: 18px;
  padding: 20px 0;
}
.cms-contact__row:last-child {
  border-bottom: 1px solid var(--seafoam-shade);
}
.cms-contact__row .k {
  color: var(--fg-3);
  flex: 0 0 120px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.14em;
  padding-top: 3px;
  text-transform: uppercase;
}
.cms-contact__row .v {
  color: var(--fg-1);
  font-size: 16px;
}
.cms-contact__row .v a {
  color: inherit;
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: border-color var(--dur) var(--ease-out);
}
.cms-contact__row .v a:hover {
  border-bottom-color: var(--claro-cocoa);
}
.cms-contact__row .v address {
  font-style: normal;
  white-space: pre-line;
}
.cms-contact__aside {
  background: #ffffff;
  border-radius: 14px;
  box-shadow: var(--shadow-2);
  padding: 44px;
}
.cms-contact__aside .cms-btn {
  justify-content: center;
  width: 100%;
}

/* ---------- Lead form ---------- */
.cms-lead-form {
  background: #ffffff;
}
.cms-lead-form__layout {
  align-items: start;
  display: grid;
  gap: 80px;
  grid-template-columns: minmax(260px, 0.84fr) minmax(360px, 1fr);
}
.cms-lead-form__intro {
  max-width: 520px;
  padding-top: 10px;
}
.cms-lead-form--layout-centered .cms-lead-form__layout {
  display: block;
  margin: 0 auto;
  max-width: 680px;
  text-align: center;
}
.cms-lead-form--layout-centered .cms-lead-form__intro {
  margin: 0 auto 40px;
  max-width: 640px;
  padding-top: 0;
}
.cms-lead-form--layout-centered .cms-eyebrow-row {
  justify-content: center;
}
.cms-lead-form--layout-centered .cms-eyebrow-row::after {
  display: none;
}
.cms-lead-form__intro p {
  color: var(--fg-2);
  font-size: 20px;
  font-weight: 300;
  line-height: 1.5;
  margin-top: 22px;
  max-width: 500px;
}
.cms-lead-form--layout-centered .cms-lead-form__intro p {
  margin-left: auto;
  margin-right: auto;
}
.cms-lead-form__form {
  background: var(--claro-linen);
  border: 1px solid var(--line-1);
  border-radius: 8px;
  box-shadow: var(--shadow-2);
  display: grid;
  gap: 18px;
  padding: 32px;
}
.cms-lead-form--layout-centered .cms-lead-form__form {
  margin: 0 auto;
  max-width: 560px;
  text-align: left;
}
.cms-lead-form__field {
  display: grid;
  gap: 8px;
}
.cms-lead-form__field label {
  color: var(--fg-2);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.cms-lead-form__field input,
.cms-lead-form__field textarea {
  appearance: none;
  background: #ffffff;
  border: 1px solid var(--line-2);
  border-radius: 4px;
  color: var(--fg-1);
  font: inherit;
  min-height: 50px;
  padding: 12px 14px;
  transition: border-color var(--dur) var(--ease-out), box-shadow var(--dur) var(--ease-out);
  width: 100%;
}
.cms-lead-form__field textarea {
  resize: vertical;
}
.cms-lead-form__field input:focus,
.cms-lead-form__field textarea:focus {
  border-color: var(--claro-aqua);
  box-shadow: 0 0 0 3px rgba(105, 200, 207, 0.28);
  outline: none;
}
.cms-lead-form__status {
  color: var(--fg-2);
  font-size: 14px;
  min-height: 22px;
  white-space: pre-line;
}
.cms-lead-form__status[data-state="success"] {
  color: #2f6f4e;
}
.cms-lead-form__status[data-state="error"] {
  color: #9d362b;
}
.cms-lead-form__form .cms-btn {
  justify-content: center;
  width: 100%;
}
.cms-lead-form__form .cms-btn:disabled {
  cursor: wait;
  opacity: 0.64;
}

/* ---------- Disclosure ---------- */
.cms-disclosure {
  background: var(--claro-linen);
  padding: 64px 0;
}
.cms-disclosure .cms-wrap {
  max-width: 980px;
}
.cms-disclosure__version {
  color: var(--fg-2);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.14em;
  margin-bottom: 18px;
  text-transform: uppercase;
}
.cms-disclosure p {
  color: var(--fg-3);
  font-size: 12px;
  line-height: 1.7;
  margin: 0 0 14px;
}

/* ---------- Long-form content ---------- */
.cms-longform-page {
  min-height: 100vh;
}
.cms-longform-index,
.cms-longform-hero {
  background: var(--claro-linen);
}
.cms-longform-back {
  border-bottom: 1px solid currentColor;
  color: var(--fg-2);
  display: inline-flex;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0;
  margin-bottom: 28px;
  padding-bottom: 4px;
  text-decoration: none;
  text-transform: uppercase;
}
.cms-longform-title {
  font-family: var(--font-serif);
  font-size: 72px;
  font-weight: 300;
  letter-spacing: 0;
  line-height: 1.02;
  margin: 0;
  max-width: 900px;
}
.cms-longform-dek {
  color: var(--fg-2);
  font-size: 22px;
  line-height: 1.5;
  margin-top: 28px;
  max-width: 760px;
}
.cms-longform-list {
  display: grid;
  gap: 0;
  margin-top: 72px;
}
.cms-longform-card {
  border-top: 1px solid var(--line-1);
  display: grid;
  gap: 14px;
  padding: 32px 0;
}
.cms-longform-card:last-child {
  border-bottom: 1px solid var(--line-1);
}
.cms-longform-card__meta {
  color: var(--fg-3);
  font-size: 13px;
  letter-spacing: 0;
  line-height: 1.4;
  min-height: 18px;
  text-transform: uppercase;
}
.cms-longform-card h2 {
  font-size: 38px;
}
.cms-longform-card a {
  color: var(--fg-1);
  text-decoration: none;
}
.cms-longform-card a:hover {
  color: var(--aqua-shade);
}
.cms-longform-card p {
  color: var(--fg-2);
  font-size: 18px;
  max-width: 760px;
}
.cms-longform-hero__grid {
  align-items: center;
  display: grid;
  gap: 56px;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 420px);
}
.cms-longform-hero__image {
  margin: 0;
}
.cms-longform-hero__image img,
.cms-longform-prose figure img {
  border-radius: 4px;
  display: block;
  height: auto;
  width: 100%;
}
.cms-longform-hero__image figcaption,
.cms-longform-prose figcaption {
  color: var(--fg-3);
  font-size: 13px;
  margin-top: 10px;
}
.cms-longform-prose {
  max-width: 780px;
}
.cms-longform-prose h2,
.cms-longform-prose h3 {
  font-family: var(--font-serif);
  font-weight: 300;
  letter-spacing: 0;
  line-height: 1.14;
  margin: 56px 0 18px;
}
.cms-longform-prose h2 {
  font-size: 42px;
}
.cms-longform-prose h3 {
  font-size: 30px;
}
.cms-longform-prose p {
  color: var(--fg-2);
  font-size: 20px;
  line-height: 1.7;
  margin: 0 0 26px;
}
.cms-longform-prose blockquote {
  border-left: 3px solid var(--claro-aqua);
  margin: 44px 0;
  padding-left: 28px;
}
.cms-longform-prose blockquote p {
  color: var(--fg-1);
  font-family: var(--font-serif);
  font-size: 34px;
  line-height: 1.25;
}
.cms-longform-prose cite {
  color: var(--fg-3);
  display: block;
  font-size: 14px;
  font-style: normal;
  letter-spacing: 0;
  margin-top: 12px;
  text-transform: uppercase;
}
.cms-longform-prose figure {
  margin: 44px 0;
}
.cms-longform-image-placeholder {
  aspect-ratio: 16 / 9;
  background: color-mix(in srgb, var(--surface-2) 82%, var(--claro-aqua));
  border: 1px solid var(--border);
  border-radius: 4px;
  width: 100%;
}
.cms-longform-cta {
  margin-top: 40px;
}

/* ---------- Responsive ---------- */
@media (max-width: 980px) {
  .cms-hero__layout {
    gap: 48px;
    padding: 96px 0 88px;
  }
}
@media (max-width: 900px) {
  .cms-section {
    padding: 96px 0;
  }
  .cms-hero__layout,
  .cms-advisor-profile__layout,
  .cms-media-text__layout,
  .cms-quote-section__layout,
  .cms-resource-list__layout,
  .cms-important-disclosures__layout,
  .cms-faq__layout,
  .cms-contact__layout,
  .cms-lead-form__layout,
  .cms-longform-hero__grid {
    grid-template-columns: 1fr;
    gap: 48px;
  }
  .cms-quote-section--no-image .cms-quote-section__layout {
    grid-template-columns: 1fr;
  }
  .cms-info-card-grid--layout-left .cms-wrap {
    grid-template-columns: 1fr;
    gap: 48px;
  }
  .cms-media-text--image-left .cms-media-text__content {
    order: 0;
  }
  .cms-info-card-grid--layout-columns .cms-info-card-grid__grid {
    display: grid;
    gap: 28px;
    grid-template-columns: 1fr;
  }
  .cms-info-card-grid--layout-columns .cms-info-card-grid__item {
    border-left: 0;
    border-top: 1px solid var(--line-2);
    flex-basis: auto;
    padding: 28px 0 0;
  }
  .cms-info-card-grid--layout-columns .cms-info-card-grid__item:first-child {
    border-top: 0;
    padding-top: 0;
  }
  .cms-info-card-grid--tone-cocoa.cms-info-card-grid--layout-columns .cms-info-card-grid__item {
    border-top-color: var(--line-on-dark);
  }
  .cms-info-card-grid--tone-seafoam.cms-info-card-grid--layout-columns .cms-info-card-grid__item {
    border-top-color: var(--seafoam-shade);
  }
  .cms-narrative__pillars {
    grid-template-columns: 1fr;
  }
  .cms-narrative__pillar {
    border-left: 0;
    border-top: 1px solid var(--seafoam-shade);
    padding: 32px 0 0;
  }
  .cms-narrative__pillar:first-child {
    border-top: 0;
    padding-top: 0;
  }
}
@media (max-width: 860px) {
  .cms-services__grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .cms-info-card-grid--layout-grid .cms-info-card-grid__grid {
    grid-template-columns: repeat(4, 1fr);
  }
  .cms-info-card-grid--layout-grid .cms-info-card-grid__item,
  .cms-info-card-grid--layout-grid .cms-info-card-grid__item:nth-child(3n + 1):last-child,
  .cms-info-card-grid--layout-grid .cms-info-card-grid__item:nth-child(3n + 1):nth-last-child(2) {
    grid-column: span 2;
  }
  .cms-info-card-grid--layout-grid .cms-info-card-grid__item:nth-child(2n + 1):last-child {
    grid-column: 2 / span 2;
  }
  .cms-info-card-grid--layout-centered .cms-info-card-grid__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 720px) {
  .cms-audiences__grid {
    grid-template-columns: 1fr;
  }
  .cms-process-steps__item,
  .cms-resource-list__item,
  .cms-info-card-grid--layout-rule-list .cms-info-card-grid__item {
    grid-template-columns: 1fr;
  }
  .cms-info-card-grid--layout-left .cms-info-card-grid__grid {
    grid-template-columns: 1fr;
  }
  .cms-resource-list__item a {
    white-space: normal;
  }
  .cms-longform-title {
    font-size: 42px;
  }
  .cms-longform-dek {
    font-size: 19px;
  }
  .cms-longform-card h2 {
    font-size: 30px;
  }
  .cms-longform-prose h2 {
    font-size: 32px;
  }
  .cms-longform-prose h3 {
    font-size: 25px;
  }
  .cms-longform-prose blockquote p {
    font-size: 28px;
  }
  .cms-wrap {
    padding: 0 24px;
  }
  .cms-lead-form__form {
    padding: 24px;
  }
}
@media (max-width: 560px) {
  .cms-site-header__inner {
    min-height: 76px;
  }
  .cms-site-header__logo {
    width: 178px;
  }
  .cms-site-footer {
    padding: 44px 0 32px;
  }
  .cms-services__grid,
  .cms-info-card-grid--layout-grid .cms-info-card-grid__grid,
  .cms-info-card-grid--layout-columns .cms-info-card-grid__grid,
  .cms-info-card-grid--layout-centered .cms-info-card-grid__grid {
    grid-template-columns: 1fr;
  }
  .cms-info-card-grid--layout-grid .cms-info-card-grid__grid[data-card-count="4"] {
    grid-template-columns: 1fr;
  }
  .cms-info-card-grid--layout-grid .cms-info-card-grid__item,
  .cms-info-card-grid--layout-grid .cms-info-card-grid__item:nth-child(2n + 1):last-child,
  .cms-info-card-grid--layout-grid .cms-info-card-grid__item:nth-child(3n + 1):last-child,
  .cms-info-card-grid--layout-grid .cms-info-card-grid__item:nth-child(3n + 1):nth-last-child(2) {
    grid-column: auto;
  }
  .cms-faq__item p {
    padding-right: 0;
  }
}
`;
